import { ApolloError } from '@apollo/client'
import {
  KPI_LOCK_DRAFT_UNREADABLE,
  KPI_LOCK_DRAFT_UNREADABLE_MATCH,
  KPI_LOCK_UNAVAILABLE,
  KPI_SUBJECT_LOCKED,
  WAVE_NOT_DRAFT,
  WAVE_NOT_DRAFT_MATCH,
  refusalCodeOf
} from '../../../apollo/waveLockLink'
import { LOOPED_SECTION_KPI_REASON } from './kpiImpact'

export interface KpiError {
  message: string
  /** The server state differs from the cache, so reload the questionnaire. */
  refetch: boolean
}

interface KpiErrorRule extends KpiError {
  /** The server's extensions.code for the refusal. */
  codes: string[]
  /** For an error without a code. */
  match: RegExp
}

export const KPI_LOCK_UNAVAILABLE_MESSAGE =
  "We couldn't check whether this wave has launched, so nothing was saved. Please try again."
export const KPI_LOCK_DRAFT_UNREADABLE_MESSAGE =
  "This wave's questionnaire can't be read, so nothing was saved. Contact support."
export const KPI_SUBJECT_LOCKED_MESSAGE =
  'This wave has launched, so its KPI questions can no longer change.'

// Server messages name raw ids, so each refusal gets wording of its own and the rest a generic message.
const RULES: KpiErrorRule[] = [
  {
    codes: [KPI_SUBJECT_LOCKED],
    match: /and this wave has launched, so (it|they) can no longer change/,
    message: KPI_SUBJECT_LOCKED_MESSAGE,
    refetch: false
  },
  {
    codes: [WAVE_NOT_DRAFT],
    match: WAVE_NOT_DRAFT_MATCH,
    message:
      'This wave has launched in at least one market, so its KPIs can no longer change.',
    refetch: false
  },
  {
    codes: [KPI_LOCK_DRAFT_UNREADABLE],
    match: KPI_LOCK_DRAFT_UNREADABLE_MATCH,
    message: KPI_LOCK_DRAFT_UNREADABLE_MESSAGE,
    refetch: false
  },
  {
    codes: [KPI_LOCK_UNAVAILABLE],
    match: /whether this wave has launched can't be checked/,
    message: KPI_LOCK_UNAVAILABLE_MESSAGE,
    refetch: false
  },
  {
    codes: ['IN_LOOPED_SECTION'],
    match: /inside a looped section/,
    message: LOOPED_SECTION_KPI_REASON,
    refetch: false
  },
  {
    codes: ['MISSING_TRACKING_ID'],
    match: /has no Tracking ID/,
    message:
      "This can't be a KPI yet: it has no Tracking ID to follow it from wave to wave.",
    refetch: false
  },
  {
    codes: ['CONSTANT_SUM_NOT_MARKABLE'],
    match: /\(constant sum\)/,
    message:
      "This question now has auto-sum, and questions whose answers add up to a total can't be KPIs.",
    refetch: true
  },
  {
    codes: ['TOP_N_OUT_OF_RANGE'],
    match: /cannot count a top \d+ here/,
    message:
      'This question now ranks fewer places than this top N. Check the top N and try again.',
    refetch: true
  },
  {
    codes: [
      'TOP_N_NOT_ALLOWED',
      'TOP_N_OF_SEVERAL_OPTIONS',
      'ALL_COMPLETES_NOT_ALLOWED'
    ],
    match:
      /Only RANKED_TOP counts a top N|tracks one option, so it needs exactly one ticked|its base cannot be ALL_COMPLETES/,
    message:
      'This question has changed and no longer allows these settings. Check them and try again.',
    refetch: true
  },
  {
    codes: ['METRIC_NOT_ALLOWED'],
    match: /is not available on a/,
    message:
      'This question has changed and no longer allows this number. Check its settings and try again.',
    refetch: true
  },
  {
    codes: ['DEFAULT_OPTION_NOT_ALLOWED'],
    match: /added by the question's settings rather than stored/,
    message:
      "\"Don't know\" and other answers the question's settings add can't count in a KPI. Check the ticks and try again.",
    refetch: true
  },
  {
    codes: ['OPTIONS_NOT_FOUND', 'ROW_NOT_FOUND', 'ENTRY_NOT_FOUND'],
    match: /has no option|has no row|No question or matrix with id/,
    message: 'Some of this content was deleted. Check the ticks and try again.',
    refetch: true
  },
  {
    codes: ['ROWS_REQUIRED'],
    match: /Pick at least one row of matrix/,
    message: 'Tick at least one row to mark as a KPI.',
    refetch: false
  },
  {
    codes: ['DUPLICATE_KPI'],
    match: /already measures this on the entry/,
    message:
      'Another KPI on this question already counts exactly this. Change what this one counts, or edit that KPI instead.',
    refetch: true
  },
  {
    codes: ['KPI_MEASURE_CHANGED'],
    match: /already measures something else/,
    message:
      'This KPI was changed elsewhere and counts something else now. Check the latest version and try again.',
    refetch: true
  },
  {
    codes: ['KPI_NOT_FOUND', 'KPI_ON_ANOTHER_ENTRY'],
    match: /No KPI with id|belongs to another entry/,
    message:
      'This KPI was changed or removed elsewhere. Check the latest version and try again.',
    refetch: true
  },
  {
    codes: ['SURVEY_NOT_IN_WAVE'],
    match: /is not one of this wave's markets/,
    message:
      "This market isn't part of this wave. Reload the page and try again.",
    refetch: false
  },
  {
    codes: [],
    match: /Conflict detected/,
    message: 'The questionnaire changed while saving. Please try again.',
    refetch: true
  }
]

const errorCodesOf = (error: unknown): string[] =>
  error instanceof ApolloError
    ? error.graphQLErrors.flatMap((graphQLError) => {
        const code = refusalCodeOf(graphQLError)
        return typeof code === 'string' ? [code] : []
      })
    : []

export const isKpiSubjectLocked = (error: unknown) =>
  errorCodesOf(error).includes(KPI_SUBJECT_LOCKED)

export const isKpiLockUnavailable = (error: unknown) =>
  errorCodesOf(error).includes(KPI_LOCK_UNAVAILABLE)

export const isKpiLockDraftUnreadable = (error: unknown) =>
  errorCodesOf(error).includes(KPI_LOCK_DRAFT_UNREADABLE)

const FALLBACK_MESSAGES = {
  save: 'Could not save the KPI. Please try again.',
  remove: 'Could not remove the KPI. Please try again.',
  markRows: 'Could not mark the rows as KPIs. Please try again.'
}

export const getKpiError = (
  error: unknown,
  action: keyof typeof FALLBACK_MESSAGES
): KpiError => {
  const codes = errorCodesOf(error)
  const raw = error instanceof Error ? error.message : ''
  const rule =
    RULES.find((candidate) =>
      candidate.codes.some((code) => codes.includes(code))
    ) ?? RULES.find(({ match }) => match.test(raw))
  if (rule) {
    return { message: rule.message, refetch: rule.refetch }
  }
  return { message: FALLBACK_MESSAGES[action], refetch: false }
}

const DUPLICATE_ROW_MATCH = /already measures this on row ([^\s:]+):/

/** setRowKpisV2's DUPLICATE_KPI names the row, which the generic wording can't. */
export const getKpiRowsError = (
  error: unknown,
  rowLabelOf: (rowId: string) => string | undefined
): KpiError => {
  const raw = error instanceof Error ? error.message : ''
  const rowId = DUPLICATE_ROW_MATCH.exec(raw)?.[1]
  if (!errorCodesOf(error).includes('DUPLICATE_KPI') && !rowId) {
    return getKpiError(error, 'markRows')
  }
  const label = rowId ? rowLabelOf(rowId) : undefined
  return {
    message: label
      ? `The row "${label}" already has a KPI that counts exactly this. Untick it to mark the other rows.`
      : 'One of the ticked rows already has a KPI that counts exactly this. Untick it to mark the other rows.',
    refetch: true
  }
}

/** The new KPI saved, but the one it replaces is still there. */
export const getKpiNotReplacedError = (
  oldLabel: string | null | undefined
): KpiError => {
  const label = oldLabel?.trim()
  return {
    message: label
      ? `The new KPI is saved, but the old KPI, "${label}", could not be removed, so both are shown. Remove it if it is no longer needed.`
      : 'The new KPI is saved, but the old one could not be removed, so both are shown. Remove the old one if it is no longer needed.',
    refetch: true
  }
}

const UPDATE_ENTRY_PREFIX = /^Failed to update questionnaire entry\. /
const OPTIONS_REPLACED_CODES = [
  'KPI_OPTIONS_REPLACED',
  'UNREADABLE_KPI_OPTIONS_REPLACED'
]

// Add scale is hidden while the cache holds a KPI, so a KPI refusal means the cache is stale.
export const getAddScaleError = (error: unknown): KpiError => {
  const raw = error instanceof Error ? error.message : ''
  const isKpiRefusal =
    errorCodesOf(error).some((code) => OPTIONS_REPLACED_CODES.includes(code)) ||
    raw.includes('KPI')
  return isKpiRefusal
    ? { message: raw.replace(UPDATE_ENTRY_PREFIX, ''), refetch: true }
    : {
        message:
          'Could not replace the options with a scale. Please try again.',
        refetch: false
      }
}
